import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Database, Server, Users, FileLock2, BadgeCheck } from 'lucide-react';
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const SecurityPage = () => {
  const location = useLocation();
  const pageTitle = "Security at WebFiny | Protecting Your Data";
  const pageDescription = "Learn about WebFiny's robust security measures, compliance standards, and commitment to protecting your data when using our PDF APIs and document automation platform.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  const securityFeatures = [
    {
      icon: <Lock className="w-10 h-10 text-primary" />,
      title: "End-to-End Encryption",
      description: "All data transmitted to and from WebFiny APIs, including your files and API requests, is encrypted using industry-standard TLS 1.2+ (HTTPS). Data at rest, including temporarily stored files, is encrypted using AES-256.",
      details: ["TLS 1.2+ for data in transit", "AES-256 for data at rest", "Secure key management practices"]
    },
    {
      icon: <Database className="w-10 h-10 text-primary" />,
      title: "Secure Infrastructure",
      description: "Our platform is built on leading cloud infrastructure providers (e.g., AWS, Google Cloud) that meet rigorous security standards, including SOC 2, ISO 27001, and PCI DSS compliance.",
      details: ["Hosted in certified data centers", "Network firewalls and intrusion detection systems", "Regular vulnerability scanning and penetration testing"]
    },
    {
      icon: <Server className="w-10 h-10 text-primary" />,
      title: "API Security",
      description: "Access to WebFiny APIs is protected through unique API keys. We recommend best practices for API key management, such as storing keys securely and rotating them periodically.",
      details: ["Secure API key authentication", "Rate limiting and abuse prevention", "IP whitelisting options for enterprise plans"]
    },
    {
      icon: <FileLock2 className="w-10 h-10 text-primary" />,
      title: "Data Handling & Deletion",
      description: "Files uploaded for processing are temporarily stored only for the duration necessary to perform the requested operation. Processed files are typically deleted within minutes to a few hours, and original files shortly thereafter. We do not permanently store your processed content unless explicitly configured for specific services (e.g., archival features you opt into).",
      details: ["Temporary file storage for processing", "Automated deletion policies", "No long-term storage of processed content by default"]
    },
    {
      icon: <Users className="w-10 h-10 text-primary" />,
      title: "Access Control & Authentication",
      description: "User accounts are protected by strong password policies and options for multi-factor authentication (MFA). Enterprise plans offer role-based access control (RBAC) for granular permission management within your organization.",
      details: ["Strong password requirements", "Multi-Factor Authentication (MFA) support", "Role-Based Access Control (RBAC) for teams"]
    },
    {
      icon: <BadgeCheck className="w-10 h-10 text-primary" />,
      title: "Compliance & Certifications",
      description: "WebFiny is committed to meeting industry-standard compliance requirements. We undergo regular third-party audits and can provide documentation to support your compliance needs (e.g., for GDPR, HIPAA, CCPA).",
      details: ["SOC 2 Type II audited (in progress)", "ISO 27001 alignment", "Support for GDPR and HIPAA compliance (BAA available for enterprise)"]
    }
  ];

  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": canonicalUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
                { "@type": "ListItem", "position": 2, "name": "Security" }
              ]
            },
            "publisher": {
              "@type": "Organization",
              "name": "WebFiny",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
              }
            }
          }
        ]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <ShieldCheck className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-4">
              Security at WebFiny
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              At WebFiny, the security of your data is our highest priority. We employ a multi-layered approach to protect your information and ensure the integrity of our PDF API services.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 mb-16 md:mb-24">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card p-6 rounded-xl shadow-lg hover:shadow-primary/20 transition-shadow duration-300 flex flex-col"
              >
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-primary/10 rounded-full mr-4">
                    {feature.icon}
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">{feature.title}</h2>
                </div>
                <p className="text-muted-foreground mb-4 flex-grow">{feature.description}</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {feature.details.map((detail, i) => (
                    <li key={i} className="flex items-center">
                      <ShieldCheck className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: securityFeatures.length * 0.1 }}
            className="text-center bg-card p-8 md:p-12 rounded-xl shadow-2xl"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-6">Our Commitment to Your Security</h2>
            <p className="text-muted-foreground max-w-3xl mx-auto mb-4">
              WebFiny is dedicated to maintaining a secure environment for all our users. We continuously monitor our systems, update our security practices, and invest in technologies to protect against emerging threats.
            </p>
            <p className="text-muted-foreground max-w-3xl mx-auto mb-6">
              We also believe in transparency. If you have any security concerns or questions, or if you are a security researcher and have found a potential vulnerability, please contact us immediately at <a href="mailto:security@webfiny.com" className="text-primary hover:underline">security@webfiny.com</a>. We operate a responsible disclosure program.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <a
                href="/privacy"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 transition-colors shadow-lg hover:shadow-primary/30"
                aria-label="Read WebFiny's Privacy Policy"
              >
                Read Our Privacy Policy
              </a>
              <a
                href="/contact?subject=SecurityInquiry"
                className="inline-flex items-center justify-center px-6 py-3 border border-border text-base font-medium rounded-md text-foreground bg-muted hover:bg-muted/80 transition-colors shadow-lg hover:shadow-muted/30"
                aria-label="Contact WebFiny with a security inquiry"
              >
                Contact Security Team
              </a>
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default SecurityPage;